/**
 * Media upload API — sends files to Edge Function which proxies to Bot API → R2.
 * All images are automatically compressed to WebP by the Bot service.
 *
 * Categories & compression:
 *   avatar  → 400x400  WebP q70  (max ~30KB output)
 *   banner  → 1200x400 WebP q75  (max ~80KB output)
 *   cover   → 1200x630 WebP q75  (challenge/goal covers)
 *   post    → 1200px   WebP q85  (high quality, no crop)
 *   proof   → 800px    WebP q75  (habit proof)
 */

import { z } from 'zod'

const EDGE_URL = import.meta.env.VITE_SUPABASE_URL

const UploadResultSchema = z.object({
  url: z.string().url(),
  key: z.string(),
  size: z.number(),
  content_type: z.string(),
})

export type UploadResult = z.infer<typeof UploadResultSchema>
export type ImageCategory = 'avatar' | 'banner' | 'cover' | 'post' | 'proof' | 'dm'

function getToken(): string | null {
  return localStorage.getItem('turnifly_access_token')
}

/**
 * Upload an image file. Returns public R2 URL.
 */
export async function uploadMedia(
  file: File,
  category: ImageCategory,
): Promise<UploadResult> {
  const token = getToken()
  if (!token) throw new Error('Not authenticated')

  const formData = new FormData()
  formData.append('file', file)
  formData.append('category', category)

  const response = await fetch(
    `${EDGE_URL}/functions/v1/api/v1/media/upload`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    },
  )

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Upload failed' }))
    throw new Error((err as { error: string }).error ?? 'Upload failed')
  }

  const data: unknown = await response.json()
  return UploadResultSchema.parse(data)
}

/**
 * Delete a file from R2 by its public URL.
 */
export async function deleteMediaByUrl(publicUrl: string): Promise<void> {
  const token = getToken()
  if (!token) return

  const urlObj = new URL(publicUrl)
  const key = urlObj.pathname.startsWith('/') ? urlObj.pathname.slice(1) : urlObj.pathname
  if (!key) return

  await fetch(
    `${EDGE_URL}/functions/v1/api/v1/media/delete`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ key }),
    },
  ).catch(() => { /* non-critical */ })
}

/**
 * Upload a challenge material (audio, video, PDF, e-book).
 */
export async function uploadMaterial(file: File): Promise<UploadResult> {
  const token = getToken()
  if (!token) throw new Error('Not authenticated')

  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch(
    `${EDGE_URL}/functions/v1/api/v1/media/upload-material`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    },
  )

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Upload failed' }))
    throw new Error((err as { error: string }).error ?? 'Upload failed')
  }

  const data: unknown = await response.json()
  return UploadResultSchema.parse(data)
}
